import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import { collection, onSnapshot, query, orderBy } from "firebase/firestore";
import { QRCodeSVG } from "qrcode.react";
import { useSearchParams } from "react-router-dom";

function toMillis(timestamp) {
  if (!timestamp) return 0;
  if (typeof timestamp.toMillis === "function") {
    return timestamp.toMillis();
  }
  if (typeof timestamp.seconds === "number") {
    return timestamp.seconds * 1000;
  }
  return 0;
}

function compareBids(a, b) {
  const amountDifference = (b.amount || 0) - (a.amount || 0);
  if (amountDifference !== 0) {
    return amountDifference;
  }

  return toMillis(b.placedAt) - toMillis(a.placedAt);
}

function formatItemLabel(itemSlug, itemName) {
  if (!itemSlug) {
    return itemName;
  }

  return `${itemSlug.charAt(0).toUpperCase()}${itemSlug.slice(1)} - ${itemName}`;
}

export default function LiveScoreboard() {
  const [items, setItems] = useState([]);
  const [bids, setBids] = useState([]);
  const [searchParams] = useSearchParams();
  const isSmall = searchParams.get("small") === "true";

  useEffect(() => {
    const unsubscribeItems = onSnapshot(collection(db, "items"), (snapshot) => {
      setItems(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    });

    const unsubscribeBids = onSnapshot(
      query(collection(db, "bids"), orderBy("placedAt", "desc")),
      (snapshot) => {
        setBids(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
      }
    );

    return () => {
      unsubscribeItems();
      unsubscribeBids();
    };
  }, []);

  const itemDetailsById = Object.fromEntries(
    items.map((item) => [item.id, { name: item.name, slug: item.slug }])
  );

  const sortedItems = items
    .map((item) => {
      const itemBids = bids
        .filter((bid) => bid.itemId === item.id)
        .sort(compareBids)
        .slice(0, 5);

      return {
        ...item,
        bids: itemBids,
      };
    })
    .sort((a, b) => {
      const highestBidA = a.bids[0]?.amount || 0;
      const highestBidB = b.bids[0]?.amount || 0;
      return highestBidB - highestBidA;
    });

  const allBids = [...bids]
    .sort((a, b) => toMillis(b.placedAt) - toMillis(a.placedAt))
    .slice(0, 5)
    .map((bid) => ({
      ...bid,
      itemName: itemDetailsById[bid.itemId]?.name || "",
      itemSlug: itemDetailsById[bid.itemId]?.slug || "",
    }));

  if (isSmall) {
    return (
      <div
        className="flex bg-gray-900 text-white font-sans"
        style={{ width: "640px", height: "320px" }}
      >
        <div className="w-1/2 p-2 overflow-y-auto">
          <h2 className="text-lg font-bold text-center mb-2">
            Dernières enchères
          </h2>
          <ul className="space-y-2">
            {allBids.map((bid) => (
              <li key={bid.id} className="bg-gray-800 p-1 rounded">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold text-yellow-400 pr-2 truncate">
                    {formatItemLabel(bid.itemSlug, bid.itemName)}
                  </span>
                  <span className="text-lg font-mono text-green-400">
                    {bid.amount}€
                  </span>
                </div>
                <div className="text-lg text-gray-400 mt-1">
                  {bid.placedAt?.toDate().toLocaleTimeString()}
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="w-1/2 flex flex-col items-center justify-center bg-gray-800">
          <QRCodeSVG
            value="https://auction.lepingouin.be/"
            size={200}
            bgColor="#1f2937"
            fgColor="#ffffff"
            level="Q"
          />
          <p className="text-lg text-white mt-2 text-center p-2">
            Scannez pour participer aux enchères!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col bg-gray-900 text-white font-sans"
      style={{ width: "640px", height: "320px" }}
    >
      <div className="p-1 flex-grow overflow-y-auto">
        <h1 className="text-base font-bold text-center mb-1">
          Live des enchères
        </h1>
        {sortedItems.length === 0 ? (
          <p className="text-center text-gray-400 text-xs">Chargement...</p>
        ) : (
          <div className="grid grid-cols-4 gap-1">
            {sortedItems.map((item) => (
              <div
                key={item.id}
                className="bg-gray-800 p-1 rounded shadow-md flex flex-col h-full"
              >
                <h2 className="font-semibold text-xs mb-0.5 truncate text-yellow-400">
                  {formatItemLabel(item.slug, item.name)}
                </h2>
                <h3 className="text-xs font-medium text-gray-300 mb-0.5">
                  Offres:
                </h3>
                <div className="flex-grow overflow-y-auto">
                  {item.bids.length > 0 ? (
                    <ul className="text-xs space-y-1">
                      {item.bids.map((bid) => (
                        <li
                          key={bid.id}
                          className="flex justify-between items-center bg-gray-700 px-1 rounded py-1"
                        >
                          <span className="truncate pr-1">
                            {bid.placedAt?.toDate().toLocaleTimeString()}
                          </span>
                          <span className="font-mono font-semibold text-green-400 whitespace-nowrap">
                            {bid.amount}€
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-xs text-gray-500 italic">Aucune.</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-center items-center p-1 bg-gray-800">
        <QRCodeSVG
          value="https://auction.lepingouin.be/"
          size={64}
          bgColor="#1f2937"
          fgColor="#ffffff"
          level="Q"
        />
      </div>
    </div>
  );
}
